import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import portalApi, { fmtINR, fmtQty } from "@/lib/portalApi";
import { catColor } from "@/pages/portal/PortalOrder";
import { usePortalAuth } from "@/context/PortalAuthContext";
import SetMpinModal from "@/pages/portal/SetMpinModal";
import { ShoppingBag, TrendingUp, Coins, PackageCheck, Search, Sparkles, Download, Loader2, BellRing, Clock, Plus, FileText, ExternalLink, RotateCcw, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const invoicePdfHref = (id) => `${BACKEND_URL}/api/portal/invoices/${id}/pdf`;

const catEmoji = (name) => {
    const s = (name || "").toLowerCase();
    if (/oil|fat/.test(s)) return "🫒";
    if (/rice|flour|starch/.test(s)) return "🌾";
    if (/spice|condiment|masala/.test(s)) return "🌶️";
    if (/pulse|dal/.test(s)) return "🫘";
    if (/dairy|milk/.test(s)) return "🥛";
    if (/clean|household/.test(s)) return "🧴";
    if (/tea|coffee|beverage/.test(s)) return "☕";
    if (/snack|biscuit/.test(s)) return "🍪";
    if (/personal|care/.test(s)) return "🧼";
    return "🛒";
};

export default function PortalDashboard() {
    const { client } = usePortalAuth();
    const nav = useNavigate();
    const [reorderPicker, setReorderPicker] = useState(false);
    const [summary, setSummary] = useState(null);
    const [history, setHistory] = useState([]);
    const [items, setItems] = useState([]);
    const [nudges, setNudges] = useState([]);
    const [draft, setDraft] = useState(null);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("");
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!client) return;   // wait for portal session — avoids 401s on race
        portalApi.get("/summary").then(r => setSummary(r.data)).catch(() => {});
        portalApi.get("/history").then(r => setHistory(r.data)).catch(() => setHistory([]));
        portalApi.get("/unique-items").then(r => setItems(r.data)).catch(() => setItems([]));
        portalApi.get("/reorder-nudge").then(r => setNudges(r.data)).catch(() => setNudges([]));
        portalApi.get("/draft").then(r => setDraft(r.data)).catch(() => setDraft(null));
    }, [client]);

    const categories = useMemo(() =>
        Array.from(new Set(items.map(i => i.category))).sort(), [items]);

    const filtered = useMemo(() => {
        const safe = q.trim().toLowerCase();
        return items.filter(i =>
            (!safe || i.name.toLowerCase().includes(safe)) &&
            (!category || i.category === category));
    }, [items, q, category]);

    // Bucket filtered items by category → the dashboard "your usual items"
    // list is always grouped by category so buyers can scan at-a-glance.
    const grouped = useMemo(() => {
        const buckets = {};
        for (const it of filtered) {
            const k = it.category || "Uncategorised";
            (buckets[k] ||= []).push(it);
        }
        return Object.entries(buckets).sort(([a], [b]) => a.localeCompare(b));
    }, [filtered]);

    const downloadHistory = async () => {
        setDownloading(true);
        try {
            // Simple client-side CSV export of every line item across every invoice
            const rows = [
                ["Invoice #", "Date", "Type", "Item", "HSN", "Qty", "Unit", "Rate", "Tax %", "Line Total", "Bachat"]
            ];
            for (const inv of history) {
                for (const li of (inv.line_items || [])) {
                    rows.push([
                        inv.invoice_number, inv.invoice_date, inv.invoice_type,
                        li.name, li.hsn || "", li.qty || 0, li.unit || "",
                        li.rate || 0, li.tax_percent || 0, li.line_total || 0,
                        li.bachat || 0,
                    ]);
                }
            }
            const csv = rows.map(r => r.map(v => {
                const s = String(v ?? "");
                return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            }).join(",")).join("\n");
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `hdb-history-${(client?.name || "client").replace(/\s+/g, "_")}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } finally { setDownloading(false); }
    };

    return (
        <div className="space-y-6" data-testid="portal-dashboard">
            {/* Force-modal: nudges pre-MPIN customers to set one before doing anything else */}
            <SetMpinModal />

            {/* Hero + KPIs */}
            <div>
                <p className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-2">Welcome back</p>
                <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight" style={{fontFamily:"'Chivo',sans-serif"}}>
                    {client?.name}
                </h1>
                <p className="text-slate-500 mt-1">
                    Here's your order history, consumption trends and quick reorder shortcuts.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="portal-kpis">
                <KpiCard icon={PackageCheck} label="Orders placed" value={summary?.invoice_count ?? "—"} accent="slate" />
                <KpiCard icon={ShoppingBag} label="Total ordered" value={fmtINR(summary?.total_spent || 0)} accent="slate" />
                <KpiCard icon={Coins} label="Total Bachat (savings)" value={fmtINR(summary?.total_saved || 0)} accent="mint"
                        data-testid="portal-total-savings" />
                <KpiCard icon={TrendingUp} label="GST paid" value={fmtINR(summary?.total_tax || 0)} accent="amber" />
            </div>

            {/* Draft-in-progress resume card */}
            {draft && draft.cart && Object.keys(draft.cart).length > 0 && (
                <div className="rounded-2xl p-4 border border-amber-300 bg-amber-50/70 flex items-center gap-4"
                     data-testid="portal-draft-resume-card">
                    <div className="w-10 h-10 rounded-xl bg-amber-500 text-white grid place-items-center">
                        <Clock size={16}/>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900">Draft in progress</div>
                        <div className="text-xs text-slate-600 mt-0.5">
                            {Object.keys(draft.cart).length} items saved
                            {draft.updated_at && <> · updated {draft.updated_at.slice(11, 16)}</>}
                            {draft.custom_items?.length ? <> · {draft.custom_items.length} custom</> : null}
                        </div>
                    </div>
                    <button onClick={() => nav("/portal/order")}
                            className="px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-sm"
                            data-testid="portal-draft-resume-btn">
                        Resume →
                    </button>
                </div>
            )}

            {/* Reorder nudges — items you usually reorder that are due */}
            {nudges.filter(n => n.due).length > 0 && (
                <ReorderNudgeCard nudges={nudges.filter(n => n.due).slice(0, 6)} />
            )}

            <div className="flex flex-wrap items-center gap-3">
                <Link to="/portal/order" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-bold shadow-lg hover:bg-slate-800 transition-colors"
                      data-testid="portal-cta-order">
                    <ShoppingBag size={16} /> Place a new order
                </Link>
                <Link to="/portal/order?reorder=1" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 text-white font-bold shadow-lg hover:bg-emerald-700 transition-colors"
                      data-testid="portal-cta-reorder">
                    <Sparkles size={16} /> Reorder your last order
                </Link>
                <button onClick={() => setReorderPicker(true)}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border-2 border-emerald-600 text-emerald-700 font-bold hover:bg-emerald-50 transition-colors"
                        data-testid="portal-cta-reorder-from-past">
                    <RotateCcw size={16}/> Reorder from past
                </button>
                <button onClick={downloadHistory} disabled={downloading || history.length === 0}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-slate-300 text-slate-700 font-bold hover:bg-slate-50 disabled:opacity-50"
                        data-testid="portal-download-history">
                    {downloading ? <Loader2 className="animate-spin" size={16}/> : <Download size={16} />}
                    Download history (CSV)
                </button>
            </div>

            {/* Full purchase history — placed near the top so clients land on
                their previous invoices first, per Cambrian-Hall feedback. */}
            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="portal-history">
                <header className="p-5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="text-lg font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                            All previous orders
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">Every invoice on your account · tap to view the PDF</p>
                    </div>
                    <span className="text-xs font-bold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full"
                          data-testid="portal-history-count">
                        {history.length} invoice{history.length !== 1 ? "s" : ""}
                    </span>
                </header>
                {history.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm" data-testid="portal-history-empty">
                        Your invoices will appear here once HDB records your first order.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-4 py-2">Invoice #</th>
                                    <th className="text-left px-4 py-2">Date</th>
                                    <th className="text-left px-4 py-2">Type</th>
                                    <th className="text-right px-4 py-2">Items</th>
                                    <th className="text-right px-4 py-2">Grand Total</th>
                                    <th className="text-right px-4 py-2">Bachat</th>
                                    <th className="text-right px-4 py-2"></th>
                                    <th className="text-right px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {history.map((inv, i) => (
                                    <tr key={i} className={`hover:bg-slate-50/60 ${inv.status === "cancelled" ? "opacity-50" : ""}`}
                                        data-testid={`portal-history-row-${i}`}>
                                        <td className="px-4 py-2 font-mono text-xs text-slate-700">
                                            {inv.invoice_number}
                                            {inv.status === "cancelled" && (
                                                <span className="ml-2 text-red-600 text-[10px] font-bold">CANCELLED</span>
                                            )}
                                            {inv.historical && (
                                                <span className="ml-2 text-slate-500 text-[10px] font-bold">HISTORIC</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-slate-600">{inv.invoice_date}</td>
                                        <td className="px-4 py-2">
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                                inv.invoice_type === "B2C" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
                                            }`}>{inv.invoice_type}</span>
                                        </td>
                                        <td className="px-4 py-2 text-right text-slate-700">{(inv.line_items || []).length}</td>
                                        <td className="px-4 py-2 text-right font-bold">{fmtINR(inv.grand_total)}</td>
                                        <td className="px-4 py-2 text-right text-emerald-700 font-semibold">
                                            {inv.today_savings ? fmtINR(inv.today_savings) : "—"}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            {inv.status !== "cancelled" && (
                                                <a href={invoicePdfHref(inv.id)} target="_blank" rel="noopener noreferrer"
                                                   className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                                                   data-testid={`portal-view-invoice-${i}`}>
                                                    <FileText size={11}/> View <ExternalLink size={9}/>
                                                </a>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            {inv.status !== "cancelled" && (inv.line_items?.length || 0) > 0 && (
                                                <button onClick={() => nav(`/portal/order?reorder_id=${inv.id}`)}
                                                        className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                                        data-testid={`portal-reorder-invoice-${i}`}>
                                                    <RotateCcw size={11}/> Reorder
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Unique items + consumption pattern */}
            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid="portal-unique-items">
                <header className="p-5 border-b border-slate-100 flex flex-wrap items-center gap-3">
                    <div>
                        <h2 className="text-lg font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                            Your usual items
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Everything you've ever ordered from HDB — with your consumption pattern
                        </p>
                    </div>
                    <div className="ml-auto flex flex-wrap gap-2 items-center">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                            <input value={q} onChange={(e) => setQ(e.target.value)}
                                   className="pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-sm w-56"
                                   placeholder="Search item…" data-testid="portal-item-search" />
                        </div>
                        <select value={category} onChange={(e) => setCategory(e.target.value)}
                                className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
                                data-testid="portal-category-filter">
                            <option value="">All categories</option>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </header>
                <div className="divide-y divide-slate-100">
                    {grouped.length === 0 && (
                        <div className="p-8 text-center text-slate-400 text-sm" data-testid="portal-items-empty">
                            No items match your filter yet.
                        </div>
                    )}
                    {grouped.map(([catName, catItems]) => {
                        const col = catColor(catName);
                        return (
                        <details key={catName} open className="group"
                                 data-testid={`portal-usual-group-${catName}`}>
                            <summary className={`cursor-pointer px-5 py-3 ${col.bg} ${col.text} border-l-4 ${col.border.replace("border-", "border-l-")} flex items-center gap-3 hover:brightness-95`}>
                                <span className="text-xl">{catEmoji(catName)}</span>
                                <span className="font-black text-base uppercase tracking-wide"
                                      style={{fontFamily:"'Chivo',sans-serif"}}>{catName}</span>
                                <span className="text-[11px] opacity-70 font-bold">
                                    · {catItems.length} item{catItems.length !== 1 ? "s" : ""}
                                </span>
                                <span className="ml-auto text-[10px] opacity-60 group-open:hidden">click to expand</span>
                                <span className="ml-auto text-[10px] opacity-60 hidden group-open:inline">click to collapse</span>
                            </summary>
                            <div className="divide-y divide-slate-100">
                                {catItems.map((it, i) => <UniqueItemRow key={i} item={it} />)}
                            </div>
                        </details>
                    );})}
                </div>
            </section>

            {/* Reorder-from-past picker */}
            {reorderPicker && (
                <ReorderPicker onClose={() => setReorderPicker(false)}
                               onPick={(id) => { setReorderPicker(false); nav(`/portal/order?reorder_id=${id}`); }}/>
            )}
        </div>
    );
}

function ReorderPicker({ onClose, onPick }) {
    const [list, setList] = useState(null);
    const [q, setQ] = useState("");

    useEffect(() => {
        portalApi.get("/reorder-list?limit=50")
            .then((r) => setList(r.data))
            .catch(() => setList([]));
    }, []);

    const filtered = (list || []).filter((r) => {
        if (!q.trim()) return true;
        const s = q.toLowerCase();
        return (r.invoice_number || "").toLowerCase().includes(s) ||
               (r.preview || "").toLowerCase().includes(s) ||
               (r.invoice_date || "").includes(s);
    });

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
             onClick={onClose} data-testid="portal-reorder-picker">
            <div onClick={(e) => e.stopPropagation()}
                 className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
                <header className="p-5 border-b border-slate-100 flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                            Reorder from past
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">Pick any past invoice — we'll load its items into your cart.</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-900"
                            data-testid="portal-reorder-picker-close">
                        <X size={20}/>
                    </button>
                </header>

                <div className="p-4 border-b border-slate-100">
                    <div className="relative">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                        <input value={q} onChange={(e) => setQ(e.target.value)}
                               placeholder="Search invoice # or item name"
                               className="w-full pl-9 pr-3 py-2 text-sm border-2 border-slate-200 rounded-xl outline-none focus:border-emerald-500"
                               data-testid="portal-reorder-search"/>
                    </div>
                </div>

                <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
                    {list === null && (
                        <div className="p-8 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" size={14}/> Loading…</div>
                    )}
                    {list && filtered.length === 0 && (
                        <div className="p-8 text-center text-slate-400 text-sm" data-testid="portal-reorder-empty">
                            No past invoices match.
                        </div>
                    )}
                    {filtered.map((r) => (
                        <button key={r.id} onClick={() => onPick(r.id)}
                                className="w-full text-left p-4 hover:bg-emerald-50 transition-colors flex items-center gap-3"
                                data-testid={`portal-reorder-pick-${r.invoice_number}`}>
                            <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center text-emerald-700 shrink-0">
                                <RotateCcw size={16}/>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono font-bold text-slate-900 text-sm">{r.invoice_number}</span>
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                        {r.invoice_date}
                                    </span>
                                    <span className="text-[10px] font-bold px-1.5 rounded bg-slate-100 text-slate-700">
                                        {r.invoice_type}
                                    </span>
                                </div>
                                <div className="text-xs text-slate-500 truncate mt-0.5">
                                    {r.item_count} item{r.item_count !== 1 ? "s" : ""} · {r.preview || "—"}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="font-black text-slate-900">{fmtINR(r.grand_total)}</div>
                                <div className="text-[10px] text-emerald-700 font-bold uppercase tracking-widest">Reorder</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function KpiCard({ icon: Icon, label, value, accent, ...rest }) {
    const accentCls = {
        slate: "bg-slate-900 text-white",
        mint:  "bg-gradient-to-br from-emerald-600 to-emerald-500 text-white",
        amber: "bg-gradient-to-br from-amber-500 to-amber-400 text-white",
    }[accent] || "bg-slate-900 text-white";
    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-start gap-3" {...rest}>
            <div className={`w-10 h-10 rounded-xl grid place-items-center ${accentCls}`}>
                <Icon size={18} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
                <div className="text-xl font-black text-slate-900 truncate" style={{fontFamily:"'JetBrains Mono',monospace"}}>{value}</div>
            </div>
        </div>
    );
}

function UniqueItemRow({ item }) {
    // Build sparkline from history [{date, qty}]
    const points = (item.history || []).slice(-12);
    const maxQ = Math.max(1, ...points.map(p => Number(p.qty) || 0));
    const W = 120, H = 32, pad = 2;
    const step = points.length > 1 ? (W - pad * 2) / (points.length - 1) : 0;
    const pathD = points.map((p, i) => {
        const x = pad + i * step;
        const y = H - pad - ((Number(p.qty) || 0) / maxQ) * (H - pad * 2);
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(" ");

    return (
        <div className="p-4 flex items-center gap-4 hover:bg-slate-50/60 transition-colors" data-testid={`portal-item-row`}>
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{item.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                    {item.category} · Last: <b className="text-slate-700">{fmtQty(item.last_qty)} {item.unit}</b>
                    <span className="mx-2 text-slate-300">·</span>
                    Total lifetime: {fmtQty(item.total_qty)} {item.unit}
                    <span className="mx-2 text-slate-300">·</span>
                    Ordered {item.order_count} time{item.order_count !== 1 ? "s" : ""}
                </div>
            </div>
            {points.length > 1 && (
                <svg width={W} height={H} className="text-emerald-600 shrink-0" aria-hidden>
                    <path d={pathD} stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    {points.map((p, i) => {
                        const x = pad + i * step;
                        const y = H - pad - ((Number(p.qty) || 0) / maxQ) * (H - pad * 2);
                        return <circle key={i} cx={x} cy={y} r="1.6" fill="currentColor" />;
                    })}
                </svg>
            )}
        </div>
    );
}

/**
 * ReorderNudgeCard — items the client usually reorders that are now overdue.
 * Uses per-item cadence computed on the backend (avg gap between orders).
 * Zero pressure — informative only. Click "Add to next order" to jump to
 * the ordering flow with that item pre-added.
 */
function ReorderNudgeCard({ nudges }) {
    return (
        <div className="rounded-2xl p-4 border border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100/40"
             data-testid="portal-reorder-nudge-card">
            <div className="flex items-center gap-2 mb-3">
                <BellRing size={16} className="text-emerald-700"/>
                <h3 className="font-black text-slate-900 text-sm uppercase tracking-wide"
                    style={{fontFamily:"'Chivo',sans-serif"}}>
                    You usually reorder these — {nudges.length} due
                </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {nudges.map((n, i) => {
                    const col = catColor(n.category);
                    return (
                        <Link key={i} to={`/portal/order`}
                              className={`p-3 rounded-lg bg-white border ${col.border} hover:shadow-md transition-shadow flex items-center gap-3`}
                              data-testid={`nudge-item-${i}`}>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-900 text-sm truncate">{n.name}</div>
                                <div className="text-[11px] text-slate-500">
                                    Usually every <b>{n.avg_cadence_days}d</b> · last <b>{n.days_since_last}d</b> ago
                                    {n.overdue_by > 0 && <span className="text-red-600 font-bold"> · overdue {n.overdue_by}d</span>}
                                </div>
                                <div className="text-[10px] text-slate-400 mt-0.5">Last qty: {n.last_qty} {n.unit}</div>
                            </div>
                            <span className="text-emerald-700"><Plus size={14}/></span>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

